import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { ChevronRight, Mail } from 'react-feather';
import {
  attendanceFollowUpsApi,
  type FollowUpParticipantSummary,
  type FollowUpParticipantsResponse,
} from '../../../../services/api/attendanceFollowUps';
import { nestedGroup, type GroupByMode } from './nestedGroup';
import { useGroupByPreference } from './useGroupByPreference';

interface FollowUpParticipantListProps {
  cohortId: string;
  initialSessionId?: string | null;
  onSelectParticipant: (cohortParticipantId: string) => void;
  /** Force-refresh signal (bumped when Level 2 returns after a mutation). */
  refreshNonce?: number;
  // MED-778: When false, hide the Send batch button
  participantFeaturesEnabled?: boolean;
}

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending only' },
  { value: 'sent', label: 'Sent only' },
  { value: 'skipped', label: 'Skipped only' },
];

export function FollowUpParticipantList({
  cohortId,
  initialSessionId,
  onSelectParticipant,
  refreshNonce = 0,
  participantFeaturesEnabled = true,
}: FollowUpParticipantListProps) {
  const [data, setData] = useState<FollowUpParticipantsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [sessionFilter, setSessionFilter] = useState<string | null>(initialSessionId ?? null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useGroupByPreference();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const result = await attendanceFollowUpsApi.listParticipants(cohortId, {
        session_id: sessionFilter ?? undefined,
        status: statusFilter ?? undefined,
      });
      setData(result);
      // Drop selections that no longer exist / are no longer pending.
      setSelectedIds(prev => {
        const keep = new Set<string>();
        for (const p of result.participants) {
          if (p.pending_count > 0 && prev.has(p.cohort_participant_id)) {
            keep.add(p.cohort_participant_id);
          }
        }
        return keep;
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to load attendance follow-ups',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [cohortId, sessionFilter, statusFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh, refreshNonce]);

  const sessionOptions = useMemo(() => {
    if (!data) return [];
    const map = new Map<string, { number: number; date: string }>();
    for (const p of data.participants) {
      map.set(p.primary_session_id, {
        number: p.primary_session_number,
        date: p.primary_session_date,
      });
    }
    return Array.from(map.entries())
      .sort((a, b) => new Date(b[1].date).getTime() - new Date(a[1].date).getTime())
      .map(([id, v]) => ({ value: id, label: `Session ${v.number}` }));
  }, [data]);

  const grouped = useMemo(
    () => (data ? nestedGroup(data.participants, groupBy) : []),
    [data, groupBy],
  );

  const totalPending = data?.total_pending ?? 0;
  const totalPast = data?.total_past ?? 0;
  const totalParticipants = data?.total_participants ?? 0;
  const pendingParticipants = useMemo(
    () => (data ? data.participants.filter(p => p.pending_count > 0) : []),
    [data],
  );

  const allPendingSelected =
    pendingParticipants.length > 0 &&
    pendingParticipants.every(p => selectedIds.has(p.cohort_participant_id));

  const handleToggleAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(pendingParticipants.map(p => p.cohort_participant_id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleToggleOne = (participantId: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(participantId);
      else next.delete(participantId);
      return next;
    });
  };

  const sendSelected = async () => {
    if (selectedIds.size === 0) return;
    // Batch send needs the queue entry_ids (not participant ids), so load the
    // L2 detail for each selected participant to collect their pending entries.
    // For a cohort with dozens of selections this is N requests — acceptable
    // for now (batch bar is a rare CTA). Parallelize.
    setSending(true);
    try {
      const participantIds = Array.from(selectedIds);
      const details = await Promise.all(
        participantIds.map(pid => attendanceFollowUpsApi.getParticipantFollowUps(cohortId, pid)),
      );
      const entryIds: string[] = [];
      for (const detail of details) {
        for (const entry of detail.entries) {
          if (entry.status === 'pending' || entry.status === 'no_template') {
            entryIds.push(entry.id);
          }
        }
      }
      if (entryIds.length === 0) {
        notifications.show({
          title: 'Nothing to send',
          message: 'None of the selected participants have pending follow-ups.',
          color: 'yellow',
        });
        return;
      }

      modals.openConfirmModal({
        title: 'Send Attendance Follow-Up Emails',
        zIndex: 400,
        children: (
          <Text size="sm">
            Send {entryIds.length} follow-up email{entryIds.length !== 1 ? 's' : ''} across{' '}
            {participantIds.length} participant{participantIds.length !== 1 ? 's' : ''}? Each will
            CC the coach and use your email as reply-to.
          </Text>
        ),
        labels: {
          confirm: `Send ${entryIds.length} Email${entryIds.length !== 1 ? 's' : ''}`,
          cancel: 'Cancel',
        },
        confirmProps: { color: 'medleyGreen' },
        onConfirm: async () => {
          try {
            const result = await attendanceFollowUpsApi.sendBatch(cohortId, { entryIds });
            notifications.show({
              title: 'Emails Sent',
              message: `${result.sent_count} sent, ${result.failed_count} failed`,
              color: result.failed_count > 0 ? 'yellow' : 'green',
            });
            await refresh();
          } catch {
            notifications.show({
              title: 'Error',
              message: 'Failed to send batch',
              color: 'red',
            });
          }
        },
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Stack gap="sm" p="md">
      <Group gap="sm" wrap="wrap">
        <Select
          placeholder="All Sessions"
          data={sessionOptions}
          value={sessionFilter}
          onChange={setSessionFilter}
          clearable
          size="xs"
          w={180}
          comboboxProps={{ zIndex: 1000 }}
        />
        <Select
          data={[
            { value: 'session', label: 'Group by: Session' },
            { value: 'group', label: 'Group by: Group' },
          ]}
          value={groupBy}
          onChange={v => v && setGroupBy(v as GroupByMode)}
          allowDeselect={false}
          size="xs"
          w={200}
          comboboxProps={{ zIndex: 1000 }}
        />
        <Select
          placeholder="All Status"
          data={STATUS_OPTIONS}
          value={statusFilter}
          onChange={setStatusFilter}
          clearable
          size="xs"
          w={160}
          comboboxProps={{ zIndex: 1000 }}
        />
      </Group>

      <Text size="xs" c="dimmed">
        {totalParticipants} participant{totalParticipants !== 1 ? 's' : ''} · {totalPending}{' '}
        pending, {totalPast} past
      </Text>

      {totalPending > 0 && (
        <Group
          justify="space-between"
          p="sm"
          style={{
            border: '1px solid #E8E6E1',
            borderRadius: 8,
            background: '#FFFFFF',
          }}
        >
          <Group gap="sm">
            <Checkbox
              checked={allPendingSelected}
              indeterminate={!allPendingSelected && selectedIds.size > 0}
              onChange={e => handleToggleAll(e.currentTarget.checked)}
              color="medleyGreen"
            />
            <Text size="sm" c={selectedIds.size > 0 ? undefined : 'dimmed'}>
              {selectedIds.size > 0
                ? `${selectedIds.size} selected`
                : 'Select participants to send pending follow-ups'}
            </Text>
          </Group>
          {participantFeaturesEnabled && (
            <Button
              color="medleyGreen"
              leftSection={<Mail size={14} />}
              disabled={selectedIds.size === 0}
              loading={sending}
              onClick={sendSelected}
            >
              Send All Selected
              {selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
            </Button>
          )}
        </Group>
      )}

      {loading && !data ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : grouped.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="xl">
          No attendance follow-ups match your filters.
        </Text>
      ) : (
        <Stack gap={0}>
          {grouped.map(primary => (
            <Box key={primary.key}>
              <Group
                justify="space-between"
                px="sm"
                py="xs"
                style={{ background: '#F5F3EF', borderBottom: '1px solid #E8E6E1' }}
              >
                <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: 0.3 }}>
                  {primary.label}
                </Text>
                {primary.pendingCount > 0 && (
                  <Badge
                    size="sm"
                    variant="filled"
                    styles={{
                      root: {
                        background: 'var(--medley-flag-bg)',
                        color: 'var(--medley-flag-text)',
                      },
                    }}
                  >
                    {primary.pendingCount} pending
                  </Badge>
                )}
              </Group>

              {primary.subgroups.map(sub => (
                <Box key={sub.key}>
                  <Group
                    gap={6}
                    px="sm"
                    py={6}
                    pl={32}
                    style={{ background: '#FAF9F6', borderBottom: '1px solid #EDEBE6' }}
                  >
                    <Text size="xs" fw={600} c="dimmed">
                      {sub.label}
                    </Text>
                    {sub.hasPending && (
                      <Box
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: 3,
                          background: 'var(--medley-flag-text)',
                        }}
                      />
                    )}
                  </Group>

                  {sub.items.map(p => (
                    <ParticipantRow
                      key={p.cohort_participant_id}
                      participant={p}
                      checked={selectedIds.has(p.cohort_participant_id)}
                      onCheckboxToggle={handleToggleOne}
                      onClick={() => onSelectParticipant(p.cohort_participant_id)}
                    />
                  ))}
                </Box>
              ))}
            </Box>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

interface ParticipantRowProps {
  participant: FollowUpParticipantSummary;
  checked: boolean;
  onCheckboxToggle: (participantId: string, checked: boolean) => void;
  onClick: () => void;
}

function ParticipantRow({ participant, checked, onCheckboxToggle, onClick }: ParticipantRowProps) {
  const hasPending = participant.pending_count > 0;
  const secondary = buildSecondaryLine(participant);

  return (
    <UnstyledButton
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 12px',
        background: '#FFFFFF',
        borderBottom: '1px solid #EDEBE6',
        width: '100%',
        textAlign: 'left',
      }}
    >
      {hasPending ? (
        <Checkbox
          checked={checked}
          onChange={e => onCheckboxToggle(participant.cohort_participant_id, e.currentTarget.checked)}
          onClick={e => e.stopPropagation()}
          color="medleyGreen"
          size="sm"
        />
      ) : (
        <Box style={{ width: 20, flexShrink: 0 }} />
      )}
      <Avatar size={32} color="medleyGreen" radius="xl">
        {getInitials(participant.participant_name)}
      </Avatar>
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Group gap={4} wrap="nowrap">
          <Text fw={600} size="sm" truncate="end">
            {participant.participant_name}
          </Text>
          {participant.group_name && (
            <Text size="xs" c="dimmed">
              ({participant.group_name})
            </Text>
          )}
        </Group>
        <Text
          size="xs"
          c={hasPending ? 'var(--medley-flag-text)' : 'dimmed'}
          lineClamp={1}
          mt={2}
        >
          {secondary}
        </Text>
      </Box>
      <Group gap={6} wrap="nowrap">
        {participant.total_email_count > 1 && (
          <Badge size="sm" color="gray" variant="light">
            {participant.total_email_count} emails
          </Badge>
        )}
        {hasPending && (
          <Badge
            size="sm"
            variant="filled"
            styles={{
              root: {
                background: 'var(--medley-flag-bg)',
                color: 'var(--medley-flag-text)',
              },
            }}
          >
            PENDING
          </Badge>
        )}
        <ChevronRight size={14} color="#888" />
      </Group>
    </UnstyledButton>
  );
}

function buildSecondaryLine(p: FollowUpParticipantSummary): string {
  if (p.pending_count > 0) {
    const tier = p.primary_template_name ?? templateTierLabel(p.primary_template_tier);
    const tierSuffix = tier ? ` · ${tier}` : '';
    return `${p.primary_absence_count}${ordinalSuffix(p.primary_absence_count)} ${p.primary_absence_type}${tierSuffix}`;
  }
  const parts: string[] = [];
  if (p.past_sent_count > 0) parts.push(`${p.past_sent_count} sent`);
  if (p.past_skipped_count > 0) parts.push(`${p.past_skipped_count} skipped`);
  return parts.join(', ') || 'No follow-ups';
}

function templateTierLabel(tier: FollowUpParticipantSummary['primary_template_tier']): string {
  switch (tier) {
    case 'first_warning':
      return 'First Warning';
    case 'first_warning_with_offer':
      return 'First Warning w/ Offer';
    case 'mid_escalation':
      return 'Mid-Escalation';
    case 'deferral_offer':
      return 'Deferral Offer';
    case 'forced_deferral':
      return 'Forced Deferral';
    default:
      return '';
  }
}

function ordinalSuffix(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

function getInitials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : '';
  return (first + last).toUpperCase() || '?';
}

export default FollowUpParticipantList;
